import { store } from '../core/state-store.js';
import { bus } from '../core/event-bus.js';
import { playerManager } from '../core/player-manager.js';
import { buildSessionReport } from '../core/report-builder.js';
import { getProfiles } from '../environments/profiles.js';
import { getAllPresets } from '../presets/registry.js';
import { getPresetExpectation, evaluatePresetAgainstExpectation } from './preset-evaluator.js';

/**
 * Exposes window.VastTester -- the primary contract between the
 * testing platform and automation tools (Playwright, Selenium, etc.).
 */
export function initAutomationApi() {
  const sleep = (ms) => new Promise(r => setTimeout(r, ms));

  window.VastTester = {
    // --- Control ---
    async loadTag(url, options = {}) {
      if (options.env) {
        bus.emit('env:select', options.env);
        await new Promise(r => setTimeout(r, 200));
      }
      if (options.vastXmlInput) {
        playerManager.loadRawXml(url, {
          sourceType: options.sourceType || 'xml',
          sourceValue: options.sourceValue || '(raw XML)',
        });
      } else {
        playerManager.loadAds(url, {
          sourceType: options.sourceType || 'tag',
          sourceValue: options.sourceValue || url,
        });
      }
    },

    async loadXml(xmlString, options = {}) {
      if (options.env) {
        bus.emit('env:select', options.env);
        await new Promise(r => setTimeout(r, 200));
      }
      playerManager.loadRawXml(xmlString, {
        sourceType: options.sourceType || 'xml',
        sourceValue: options.sourceValue || '(raw XML)',
      });
    },

    play() { playerManager.play(); },
    pause() { playerManager.pause(); },
    stop() { playerManager.stop(); },
    skip() { playerManager.skip(); },

    destroy() { playerManager.destroy(); },

    setEnvironment(profileName) {
      bus.emit('env:select', profileName);
    },

    setVolume(val) { playerManager.volume = val; },
    setMuted(val) { playerManager.muted = val; },

    // --- State (read-only) ---
    getState() {
      return store.state;
    },

    getEvents() {
      return store.events;
    },

    getNetworkLog() {
      return store.networkLog;
    },

    getValidationResults() {
      return store.state.validationResults;
    },

    getSessionData() {
      return store.getSessionData();
    },

    getSessionReport() {
      return buildSessionReport();
    },

    exportReport() {
      return JSON.stringify(buildSessionReport());
    },

    resetSession() {
      store.reset();
      bus.emit('session:reset');
      return store.getSessionData();
    },

    async runScenario(input, options = {}) {
      if (input?.xml) {
        await this.loadXml(input.xml, {
          env: options.env,
          sourceType: input.sourceType || 'xml',
          sourceValue: input.sourceValue || '(raw XML)',
        });
      } else if (input?.tag) {
        await this.loadTag(input.tag, {
          env: options.env,
          sourceType: input.sourceType || 'tag',
          sourceValue: input.sourceValue || input.tag,
        });
      } else {
        throw new Error('runScenario requires either { tag } or { xml }.');
      }

      const result = await this.waitForAdComplete(options.timeoutMs || 60000);

      if (options.validate !== false) {
        bus.emit('validation:run');
      }

      return {
        result,
        report: buildSessionReport(),
      };
    },

    async evaluatePreset(presetName, options = {}) {
      const preset = getAllPresets().find(p => p.name === presetName);
      if (!preset) {
        throw new Error(`Unknown preset: ${presetName}`);
      }

      const expectation = getPresetExpectation(preset);

      // Give UI/event loops a chance to settle between runs in automation contexts.
      await sleep(options.settleBeforeMs ?? 100);

      const scenario = await this.runScenario(
        { tag: preset.url, sourceType: 'preset', sourceValue: preset.name },
        {
          env: options.env,
          timeoutMs: options.timeoutMs ?? 60000,
          validate: options.validate ?? true,
        }
      );

      const evaluation = evaluatePresetAgainstExpectation({
        preset,
        expectation,
        report: scenario.report,
      });

      return {
        preset,
        expectation,
        evaluation,
        result: scenario.result,
        report: scenario.report,
      };
    },

    async evaluateAllPresets(options = {}) {
      const presets = getAllPresets();
      const includeRemote = options.includeRemote ?? true;

      const selected = includeRemote
        ? presets
        : presets.filter((p) => !['Remote (RMP)', 'VPAID', 'SIMID'].includes(p.category));

      const results = [];
      for (const preset of selected) {
        try {
          const run = await this.evaluatePreset(preset.name, options);
          results.push({ ...run, status: run.evaluation.status });
        } catch (error) {
          results.push({
            preset,
            expectation: getPresetExpectation(preset),
            status: 'error',
            error: {
              message: error?.message || String(error),
            },
          });
        }
      }

      const summary = {
        total: results.length,
        match: results.filter(r => r.status === 'match').length,
        mismatch: results.filter(r => r.status === 'mismatch').length,
        inconclusive: results.filter(r => r.status === 'inconclusive').length,
        error: results.filter(r => r.status === 'error').length,
      };

      return { summary, results };
    },

    getAllGetters() {
      return playerManager.readAllGetters();
    },

    // --- Waiting (returns Promises for automation) ---
    waitForEvent(eventName, timeoutMs = 30000) {
      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          cleanup();
          reject(new Error(`Timeout waiting for event "${eventName}" after ${timeoutMs}ms`));
        }, timeoutMs);

        const cleanup = bus.on('ad:event', ({ type }) => {
          if (type === eventName) {
            clearTimeout(timer);
            cleanup();
            resolve({ type, timestamp: Date.now() });
          }
        });
      });
    },

    waitForState(stateName, timeoutMs = 30000) {
      return new Promise((resolve, reject) => {
        if (store.state.playerState === stateName) {
          resolve({ state: stateName });
          return;
        }

        const timer = setTimeout(() => {
          cleanup();
          reject(new Error(`Timeout waiting for state "${stateName}" after ${timeoutMs}ms`));
        }, timeoutMs);

        const cleanup = bus.on('state:changed', ({ current }) => {
          if (current.playerState === stateName) {
            clearTimeout(timer);
            cleanup();
            resolve({ state: stateName });
          }
        });
      });
    },

    waitForAdComplete(timeoutMs = 60000) {
      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          cleanup1();
          cleanup2();
          reject(new Error(`Timeout waiting for ad complete after ${timeoutMs}ms`));
        }, timeoutMs);

        const done = (type) => {
          clearTimeout(timer);
          cleanup1();
          cleanup2();
          resolve({ type, sessionData: store.getSessionData() });
        };

        const cleanup1 = bus.on('ad:event', ({ type }) => {
          if (type === 'adcomplete' || type === 'addestroyed') done(type);
        });
        const cleanup2 = bus.on('ad:event', ({ type }) => {
          if (type === 'aderror') done(type);
        });
      });
    },

    // --- Config ---
    getPresets() {
      return getAllPresets();
    },

    getPresetExpectations() {
      return getAllPresets().map(preset => ({
        preset,
        expectation: getPresetExpectation(preset),
      }));
    },

    getEnvironments() {
      return getProfiles();
    },
  };
}
